import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Funnel, ArrowUp } from "react-bootstrap-icons";
import ReactMultiSelectCheckboxes from "react-multiselect-checkboxes";
import jwt_decode from "jwt-decode";
import Axios from "axios";
import config from "react-global-configuration";
import AmCharts from "@amcharts/amcharts3-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { List } from "react-virtualized";
import { BeatLoader } from "react-spinners";
import { css } from "@emotion/core";
import "./StorePerformance.css";
import bannerTrophy from "../../assets/img/banner-trophy.svg";
import chartExpandIcon from "../../assets/img/chart_expand_icon.png";

/** Stable option reference for react-select / multiselect equality */
const ALL_TEAM_MEMBERS_OPTION = { value: "all", label: "All" };

const SP_LOG = "[StorePerformance]";

const WEEKLY_CHART_FALLBACK_TITLES = {
  overall: "Transactions Count (Overall)",
  txnType: "Transactions Count (Txn Type)",
  brand: "Transactions Count (Brand)",
  bod: "BOD Transactions Count",
};

/** Backend `weekly_model_mix` (or equivalent) — when true, add a separate effect that calls only that chart with `buildFilterPayload()` (brand/model). Core weekly_banner + four weekly_chart calls ignore brand/model. */
const WEEKLY_MODEL_MIX_CHART_ENABLED = false;


const beatLoaderOverride = css`
  display: block;
  margin: 0 auto;
  text-align: center;
`;

/** Positive (green) API insight: purple text + bullet; green arrow at end. Red: red text + red bullet. */
function WeeklyInsightBullet({ item }) {
  const isRed = item.color === "red";
  return (
    <div
      className={`sp-summary-bullet ${isRed ? "text-danger" : "sp-summary-bullet--positive"}`}
    >
      <span className="sp-summary-bullet-text">{item.text}</span>
      {!isRed && (
        <span className="sp-summary-bullet-pos-icon" aria-hidden>
          <ArrowUp />
        </span>
      )}
    </div>
  );
}

const VirtualizedMenuList = props => {
  const children = props.children;
  if (!Array.isArray(children)) {
    return <div className="no-options-message">{children}</div>;
  }
  const no_of_rows = children.length;
  const drop_down_height = no_of_rows < 9 ? 35 * no_of_rows : 300;
  const rowRenderer = ({ key, index, style }) => (
    <div key={key} style={style}>{children[index]}</div>
  );
  return (
    <List
      style={{ width: '100%' }}
      width={300}
      height={drop_down_height}
      rowHeight={35}
      rowCount={no_of_rows}
      rowRenderer={rowRenderer}
    />
  );
};

/**
 * Computes storeIds (for get-store-mgc) and personCodes (for get-pincodes) based on current filter state.
 *
 * Rules:
 *  - Specific team members selected → use their emp_ids for both.
 *  - No specific members AND managers selected → storeIds=[] (caller must pass manager codes directly
 *    to get-store-mgc), personCodes=[] (caller must pass manager codes to get-pincodes).
 *  - No specific members AND no managers → fall back to asoEmpCode (IC) or filterRootIds (admin/manager root).
 */
function getTeamScopeForStoresAndPins(selectedUsers, selectedManagers, asoEmpCode, filterRootIds, logStep) {
  const allIsSelected = (selectedUsers || []).some((u) => u.value === "all");
  const noSelection = (selectedUsers || []).length === 0;
  const specificTeamIds =
    allIsSelected || noSelection
      ? []
      : (selectedUsers || []).map((u) => u.value).filter((id) => id && id !== "all");
  const managersSelected = selectedManagers && selectedManagers.length > 0;
  let storeIds;
  if (specificTeamIds.length > 0) {
    storeIds = specificTeamIds;
  } else if (managersSelected) {
    storeIds = [];
  } else {
    storeIds = Array.isArray(filterRootIds) ? filterRootIds : [];
  }
  const personCodes = specificTeamIds.length > 0 ? specificTeamIds : storeIds;
  if (logStep) {
    console.log(`${SP_LOG} getTeamScopeForStoresAndPins [${logStep}]`, {
      allIsSelected,
      noSelection,
      specificTeamIds,
      managersSelected,
      storeIds,
      personCodes,
    });
  }
  return { storeIds, personCodes };
}

const StorePerformance = (props) => {
  const { history } = props;
  const dashboardRef = useRef(null);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [brandList, setBrandList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [expandedChart, setExpandedChart] = useState(null); // { title, options, series, type }
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  /** Single selected store option, or null */
  const [selectedStore, setSelectedStore] = useState(null);
  const [managerList, setManagerList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  /** undefined = JWT not read yet; [] = admin / no scope; [code] = manager_of_aso or aso_emp_code */
  const [filterRootIds, setFilterRootIds] = useState(undefined);
  /** IC scope from JWT — same fallback as Team Dashboard when "All" team members (empty team_ids). */
  const [asoEmpCode, setAsoEmpCode] = useState(null);
  const [isICUser, setIsICUser] = useState(false);
  const previousSelectionRef = useRef([]);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const storeDropdownRef = useRef(null);
  const storeSearchInputRef = useRef(null);
  // Brand searchable dropdown
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const brandDropdownRef = useRef(null);
  const brandSearchInputRef = useRef(null);
  // Model searchable dropdown
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const modelDropdownRef = useRef(null);
  const modelSearchInputRef = useRef(null);
  // Pin Codes multi-select searchable dropdown
  const [pinCodeList, setPinCodeList] = useState([]);
  const [selectedPinCodes, setSelectedPinCodes] = useState([]);
  const [pinCodeDropdownOpen, setPinCodeDropdownOpen] = useState(false);
  const [pinCodeSearch, setPinCodeSearch] = useState("");
  const pinCodeDropdownRef = useRef(null);
  const pinCodeSearchInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  const [transactionOverallData, setTransactionOverallData] = useState([]);
  const [transactionOverallInsight, setTransactionOverallInsight] = useState([]);
  const [txnTypeData, setTxnTypeData] = useState([]);
  const [txnTypeInsight, setTxnTypeInsight] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [brandInsight, setBrandInsight] = useState([]);
  const [bodData, setBodData] = useState([]);
  const [bodInsight, setBodInsight] = useState([]);
  const [weeklyBannerData, setWeeklyBannerData] = useState(null);
  const [weeklyBannerLoading, setWeeklyBannerLoading] = useState(false);
  const [overallLoading, setOverallLoading] = useState(false);
  const [txnTypeLoading, setTxnTypeLoading] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  const [bodLoading, setBodLoading] = useState(false);
  const [modelMixData, setModelMixData] = useState([]);
  const [modelMixInsight, setModelMixInsight] = useState("");
  const [modelMixTitle, setModelMixTitle] = useState("Pricing Insight");
  const [modelMixLoading, setModelMixLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [weeklyChartTitles, setWeeklyChartTitles] = useState(() => ({ ...WEEKLY_CHART_FALLBACK_TITLES }));
  /** Avoid duplicate weekly chart/banner calls before loadFilters finishes and store auto-select can run */
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  /** True while a manager/team change is in-flight (awaiting fetchStores); blocks chart effect mid-update */
  const [isFilterChanging, setIsFilterChanging] = useState(false);


  /** Drop selection if store list no longer contains it (e.g. refetched for another team). */
  useEffect(() => {
    if (selectedStore == null || !storeList.length) return;
    const ok = storeList.some((s) => String(s.value) === String(selectedStore.value));
    if (!ok) setSelectedStore(null);
  }, [storeList, selectedStore]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem("usertoken");
      if (!token) {
        console.log(`${SP_LOG} JWT: no usertoken, scope cleared`);
        setFilterRootIds([]);
        setAsoEmpCode(null);
        setIsICUser(false);
        return;
      }

      const decoded = jwt_decode(token);
      const isIC = !!decoded?.aso_emp_code;
      setIsICUser(isIC);
      setAsoEmpCode(decoded?.aso_emp_code || null);
      const isAdminUser = !decoded?.aso_emp_code && !decoded?.manager_of_aso;
      let nextRoot = [];
      if (isAdminUser) {
        nextRoot = [];
      } else if (decoded?.manager_of_aso) {
        nextRoot = [decoded.manager_of_aso];
      } else if (decoded?.aso_emp_code) {
        nextRoot = [decoded.aso_emp_code];
      } else {
        nextRoot = [];
      }
      setFilterRootIds(nextRoot);
      console.log(`${SP_LOG} JWT scope`, {
        isICUser: isIC,
        isAdminUser,
        hasManagerOfAso: !!decoded?.manager_of_aso,
        filterRootIds: nextRoot,
        asoEmpCode: decoded?.aso_emp_code || null,
      });
    } catch (error) {
      console.error("Error decoding JWT:", error);
      setFilterRootIds([]);
      setAsoEmpCode(null);
      setIsICUser(false);
    }
  }, []);

  const fetchManagers = useCallback(async (asoEmpCodes) => {
    const idsPayload = Array.isArray(asoEmpCodes) ? asoEmpCodes : [];
    console.log(`${SP_LOG} fetchManagers → POST .../get-managers`, { manager_code: idsPayload });
    try {
      setLoading(true);
      const response = await Axios.post(
        config.get("API_URL") + "/filters/genie_dashboard/get-managers",
        { manager_code: idsPayload },
        { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      console.log(`${SP_LOG} fetchManagers ✓`, { managerCount: rows.length });
      setManagerList(
        rows.map((manager) => ({
          value: manager.emp_id,
          label: manager.manager_name,
        }))
      );
    } catch (error) {
      console.error("Error fetching managers:", error);
      setManagerList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTeamMembers = useCallback(async (asoEmpCodes, managerCodes = []) => {
    const idsPayload = Array.isArray(asoEmpCodes) ? asoEmpCodes : [];
    const managerCodesPayload = Array.isArray(managerCodes) ? managerCodes : [];
    console.log(`${SP_LOG} fetchTeamMembers → POST .../mgc-user`, {
      ids: idsPayload,
      manager_code: managerCodesPayload,
    });
    try {
      setLoading(true);
      const response = await Axios.post(
        config.get("API_URL") + "/filters/genie_dashboard/mgc-user",
        { ids: idsPayload, manager_code: managerCodesPayload },
        { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      console.log(`${SP_LOG} fetchTeamMembers ✓`, { teamMemberCount: rows.length });
      const formattedData = rows.map((user) => ({
        value: user.emp_id,
        label: user.person_name || `${user.emp_id}`,
      }));
      setUserList([ALL_TEAM_MEMBERS_OPTION, ...formattedData]);
    } catch (error) {
      console.error("Error fetching team members:", error);
      setUserList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStores = useCallback(async (ids, pinCodes = []) => {
    if (!Array.isArray(ids)) {
      console.log(`${SP_LOG} fetchStores skipped (ids not an array)`, { ids });
      setStoreList([]);
      return;
    }
    const pin_codes = Array.isArray(pinCodes) ? pinCodes : [];
    console.log(`${SP_LOG} fetchStores → POST .../get-store-mgc`, { ids, pin_codes });
    try {
      setLoading(true);
      const response = await Axios.post(
        config.get("API_URL") + "/filters/genie_dashboard/get-store-mgc",
        { ids, pin_codes },
        { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      console.log(`${SP_LOG} fetchStores ✓`, { storeCount: rows.length });
      setStoreList(
        rows.map((store) => ({
          value: store.store_code || store.store_name,
          label: store.store_name || store.store_code,
        }))
      );
    } catch (error) {
      console.error("Error fetching stores:", error);
      setStoreList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPinCodes = useCallback(async (personCodes) => {
    const codes = Array.isArray(personCodes) ? personCodes : [];
    console.log(`${SP_LOG} fetchPinCodes → POST .../get-pincodes`, {
      person_codes: codes,
      note: codes.length === 0 ? "same scope as get-store-mgc ids when All / no member" : undefined,
    });
    try {
      setLoading(true);
      const response = await Axios.post(
        config.get("API_URL") + "/filters/get-pincodes",
        { person_codes: codes },
        { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      // Deduplicate by pin_code
      const seen = new Set();
      const unique = [];
      rows.forEach((r) => {
        if (r.pin_code && !seen.has(r.pin_code)) {
          seen.add(r.pin_code);
          unique.push({ value: r.pin_code, label: r.pin_code });
        }
      });
      console.log(`${SP_LOG} fetchPinCodes ✓`, { rowCount: rows.length, uniquePinCount: unique.length });
      setPinCodeList(unique);
    } catch (error) {
      console.error("Error fetching pin codes:", error);
      setPinCodeList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (filterRootIds === undefined) return; // wait for JWT decode
    const loadFilters = async () => {
      console.log(`${SP_LOG} loadFilters start`, {
        filterRootIds,
        asoEmpCode,
        storeIdsForInitialStores: filterRootIds,
      });
      setFiltersHydrated(false);
      setLoading(true);
      try {
        console.log(`${SP_LOG} loadFilters step 1/4: parallel get-managers, mgc-user, get-store-mgc, get-pincodes`);
        const { personCodes } = getTeamScopeForStoresAndPins(
          [ALL_TEAM_MEMBERS_OPTION],
          [],
          asoEmpCode,
          filterRootIds,
          "loadFilters"
        );
        await Promise.all([
          fetchManagers(filterRootIds),
          fetchTeamMembers(filterRootIds, []),
          fetchStores(filterRootIds),
          fetchPinCodes(personCodes),
        ]);
        console.log(`${SP_LOG} loadFilters step 2/4: set MGC team selection to All`);
        setSelectedUsers([ALL_TEAM_MEMBERS_OPTION]);
        previousSelectionRef.current = [ALL_TEAM_MEMBERS_OPTION];
        console.log(`${SP_LOG} loadFilters step 3/4: complete (brands deferred until store selected)`);
      } finally {
        setLoading(false);
        setFiltersHydrated(true);
        console.log(`${SP_LOG} loadFilters: filtersHydrated=true`);
      }
    };
    loadFilters();
  }, [filterRootIds, asoEmpCode, fetchManagers, fetchTeamMembers, fetchStores, fetchPinCodes]);

  // Re-fetch brands when selected store changes
  useEffect(() => {
    if (!filtersHydrated) return;
    if (!selectedStore) {
      setBrandList([]);
      return;
    }
    const storeCode = selectedStore.value;
    const fetchBrands = async () => {
      try {
        const res = await Axios.post(
          config.get("API_URL") + "/filters/get-brands",
          { store_code: storeCode },
          { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
        );
        if (res?.data) setBrandList(res.data);
      } catch (e) {
        console.error("Error fetching brands:", e);
        setBrandList([]);
      }
    };
    // Reset brand/model selections when store changes
    setSelectedBrand("");
    setSelectedModel("");
    setModelList([]);
    setBrandSearch("");
    setModelSearch("");
    fetchBrands();
  }, [selectedStore, filtersHydrated]);

  useEffect(() => {
    if (!selectedBrand) {
      setModelList([]);
      setSelectedModel("");
      return;
    }
    const storeCode = selectedStore?.value ?? "";
    const fetchModels = async () => {
      try {
        const response = await Axios.post(
          config.get("API_URL") + "/filters/get-models",
          { brand: selectedBrand, store_code: storeCode },
          { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
        );
        if (response?.data) {
          setModelList(response.data);
          setSelectedModel("");
          setModelSearch("");
        }
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };
    fetchModels();
  }, [selectedBrand, selectedStore]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      padding: "0px",
      minHeight: "15px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "23px",
    }),
    clearIndicator: (provided) => ({ ...provided, padding: "5px" }),
    dropdownIndicator: (provided) => ({ ...provided, padding: "5px" }),
    valueContainer: (base) => ({
      ...base,
      padding: "0px 6px",
    }),
    menu: (styles) => ({ ...styles, zIndex: 9999 }),
  };

  const handleManagersChange = async (selectedOptions) => {
    const newSelection = selectedOptions || [];
    console.log(`${SP_LOG} handleManagersChange start`, {
      selectedManagerIds: newSelection.map((m) => m.value),
    });
    setIsFilterChanging(true);
    setSelectedManagers(newSelection);

    // Clear dependent filters
    setSelectedUsers([]);
    previousSelectionRef.current = [];
    setSelectedStore(null);
    setSelectedPinCodes([]);
    setPinCodeList([]);

    if (newSelection.length > 0) {
      const managerCodes = newSelection.map(m => m.value);
      console.log(`${SP_LOG} handleManagersChange: branch with managers → mgc-user + get-store-mgc`, {
        managerCodes,
      });
      await fetchTeamMembers([], managerCodes);
      setSelectedUsers([ALL_TEAM_MEMBERS_OPTION]);
      previousSelectionRef.current = [ALL_TEAM_MEMBERS_OPTION];
      await Promise.all([
        fetchStores(managerCodes, []),
        fetchPinCodes(managerCodes),
      ]);
    } else {
      const rootIds = Array.isArray(filterRootIds) ? filterRootIds : [];
      console.log(`${SP_LOG} handleManagersChange: branch no managers → reset team list + get-store-mgc`, {
        rootIds,
      });
      await fetchTeamMembers(rootIds, []);
      setSelectedUsers([ALL_TEAM_MEMBERS_OPTION]);
      previousSelectionRef.current = [ALL_TEAM_MEMBERS_OPTION];
      const { storeIds, personCodes } = getTeamScopeForStoresAndPins(
        [ALL_TEAM_MEMBERS_OPTION],
        [],
        asoEmpCode,
        filterRootIds,
        "managersClearReset"
      );
      await Promise.all([
        fetchStores(storeIds, []),
        fetchPinCodes(personCodes),
      ]);
    }
    console.log(`${SP_LOG} handleManagersChange done`);
    setIsFilterChanging(false);
  };

  const handleTeamMembersChange = async (selectedOptions) => {
    const newOptions = selectedOptions || [];
    const oldAllSelected = selectedUsers.some(u => u.value === 'all');
    const newAllSelected = newOptions.some(u => u.value === 'all');
    const previousSelection = previousSelectionRef.current;

    let finalSelection = [];

    if (!oldAllSelected && newAllSelected) {
      finalSelection = [ALL_TEAM_MEMBERS_OPTION];
    } else if (oldAllSelected && !newAllSelected) {
      const nonAllOptions = newOptions.filter(u => u.value !== 'all');
      const previousNonAll = previousSelection.filter(u => u.value !== 'all');
      if (nonAllOptions.length >= userList.length - 1) {
        finalSelection = [nonAllOptions[nonAllOptions.length - 1]];
      } else {
        const newlyAdded = nonAllOptions.filter(
          newOpt => !previousNonAll.some(prev => prev.value === newOpt.value)
        );
        finalSelection = newlyAdded.length > 0 ? newlyAdded : nonAllOptions;
      }
    } else if (oldAllSelected && newAllSelected && newOptions.length > 1) {
      finalSelection = newOptions.filter(u => u.value !== 'all');
    } else if (newOptions.length === 0) {
      finalSelection = [];
    } else {
      finalSelection = newOptions;
    }

    console.log(`${SP_LOG} handleTeamMembersChange: resolved selection`, {
      finalSelection: finalSelection.map((u) => ({ value: u.value, label: u.label })),
    });
    setIsFilterChanging(true);
    previousSelectionRef.current = finalSelection;
    setSelectedUsers(finalSelection);
    setSelectedStore(null);
    // Reset pin codes when team member changes
    setSelectedPinCodes([]);
    setPinCodeList([]);
    setPinCodeSearch("");

    const { storeIds, personCodes } = getTeamScopeForStoresAndPins(
      finalSelection,
      selectedManagers,
      asoEmpCode,
      filterRootIds,
      "teamMembersChange"
    );
    // Fallback: when managers are selected but no specific team members are chosen,
    // getTeamScopeForStoresAndPins returns [] expecting the caller to supply manager codes.
    const managersSelected = selectedManagers && selectedManagers.length > 0;
    const noSpecificMembers = finalSelection.length === 0 || finalSelection.every(u => u.value === 'all');
    const managerCodes = managersSelected ? selectedManagers.map(m => m.value) : [];
    const effectivePersonCodes = (personCodes.length === 0 && managersSelected && noSpecificMembers) ? managerCodes : personCodes;
    const effectiveStoreIds = (storeIds.length === 0 && managersSelected && noSpecificMembers) ? managerCodes : storeIds;
    console.log(`${SP_LOG} handleTeamMembersChange: parallel fetchPinCodes + fetchStores (pins cleared, no pin filter on stores)`);
    await Promise.all([
      fetchPinCodes(effectivePersonCodes),
      fetchStores(effectiveStoreIds, []),
    ]);
    console.log(`${SP_LOG} handleTeamMembersChange done`);
    setIsFilterChanging(false);
  };

  // Handle pin code selection (multi-select)
  const handlePinCodeToggle = async (pinCode) => {
    const already = selectedPinCodes.includes(pinCode);
    const newPinCodes = already
      ? selectedPinCodes.filter((p) => p !== pinCode)
      : [...selectedPinCodes, pinCode];
    console.log(`${SP_LOG} handlePinCodeToggle`, { pinCode, already, newPinCodes });
    setSelectedPinCodes(newPinCodes);
    setSelectedStore(null);

    const allIsSelected = (selectedUsers || []).some(u => u.value === 'all');
    const noSelection = (selectedUsers || []).length === 0;
    const specificTeamIds = (allIsSelected || noSelection)
      ? []
      : (selectedUsers || []).map(u => u.value).filter(id => id && id !== 'all');
    const managersSelected = selectedManagers && selectedManagers.length > 0;
    let storeIds;
    if (specificTeamIds.length > 0) {
      storeIds = specificTeamIds;
    } else if (managersSelected) {
      storeIds = (selectedManagers || []).map(m => m.value);
    } else {
      storeIds = Array.isArray(filterRootIds) ? filterRootIds : [];
    }
    console.log(`${SP_LOG} handlePinCodeToggle: refetch stores with pin filter`, { storeIds, pin_codes: newPinCodes });
    await fetchStores(storeIds, newPinCodes);
  };

  const handleStoreChange = (opt) => {
    setSelectedStore(opt || null);
    setStoreDropdownOpen(false);
    setStoreSearch("");
  };

  const handleResetFilters = async () => {
    setIsFilterChanging(true);
    setSelectedManagers([]);
    setSelectedUsers([]);
    previousSelectionRef.current = [];
    setSelectedStore(null);
    setSelectedPinCodes([]);
    setPinCodeList([]);
    setPinCodeSearch("");
    setSelectedBrand("");
    setSelectedModel("");
    setModelList([]);
    const rootIds = Array.isArray(filterRootIds) ? filterRootIds : [];
    await fetchTeamMembers(rootIds, []);
    setSelectedUsers([ALL_TEAM_MEMBERS_OPTION]);
    previousSelectionRef.current = [ALL_TEAM_MEMBERS_OPTION];
    const { storeIds, personCodes } = getTeamScopeForStoresAndPins(
      [ALL_TEAM_MEMBERS_OPTION],
      [],
      asoEmpCode,
      filterRootIds,
      "resetFilters"
    );
    await Promise.all([
      fetchStores(storeIds, []),
      fetchPinCodes(personCodes),
    ]);
    setIsFilterChanging(false);
  };

  // Close store dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(e.target)) {
        setStoreDropdownOpen(false);
        setStoreSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (storeDropdownOpen && storeSearchInputRef.current) {
      storeSearchInputRef.current.focus();
    }
  }, [storeDropdownOpen]);

  const filteredStores = useMemo(() => {
    const q = storeSearch.trim().toLowerCase();
    if (!q) return storeList;
    return storeList.filter((s) => s.label.toLowerCase().includes(q));
  }, [storeList, storeSearch]);

  // Close brand dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target)) {
        setBrandDropdownOpen(false);
        setBrandSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus brand search input when dropdown opens
  useEffect(() => {
    if (brandDropdownOpen && brandSearchInputRef.current) {
      brandSearchInputRef.current.focus();
    }
  }, [brandDropdownOpen]);

  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    if (!q) return brandList;
    return brandList.filter((b) => (b.brand || "").toLowerCase().includes(q));
  }, [brandList, brandSearch]);

  // Close model dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setModelDropdownOpen(false);
        setModelSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus model search input when dropdown opens
  useEffect(() => {
    if (modelDropdownOpen && modelSearchInputRef.current) {
      modelSearchInputRef.current.focus();
    }
  }, [modelDropdownOpen]);

  const filteredModels = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    if (!q) return modelList;
    return modelList.filter((m) => (m.txn_model_name || "").toLowerCase().includes(q));
  }, [modelList, modelSearch]);

  // Close pin code dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pinCodeDropdownRef.current && !pinCodeDropdownRef.current.contains(e.target)) {
        setPinCodeDropdownOpen(false);
        setPinCodeSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus pin code search input when dropdown opens
  useEffect(() => {
    if (pinCodeDropdownOpen && pinCodeSearchInputRef.current) {
      pinCodeSearchInputRef.current.focus();
    }
  }, [pinCodeDropdownOpen]);

  const filteredPinCodes = useMemo(() => {
    const q = pinCodeSearch.trim().toLowerCase();
    if (!q) return pinCodeList;
    return pinCodeList.filter((p) => p.label.toLowerCase().includes(q));
  }, [pinCodeList, pinCodeSearch]);

  const buildCoreWeeklyPayload = useCallback(() => {
    const manager_codes = (selectedManagers || []).map(m => m.value).filter(Boolean);
    const allSelected = selectedUsers.some(u => u.value === 'all');
    const rawTeamIds = (allSelected || selectedUsers.length === 0)
      ? []
      : selectedUsers.map(u => u.value).filter(id => id && id !== 'all');
    // Match Team Dashboard genie_team_dashboard: when "All" / empty selection, fall back to IC aso_emp_code
    const team_ids =
      rawTeamIds.length === 0 && asoEmpCode ? [asoEmpCode] : rawTeamIds;
    const store_codes =
      selectedStore != null
        ? [selectedStore.value ?? selectedStore.label].filter((x) => x != null && x !== "")
        : [];
    return { manager_codes, team_ids, store_codes, brand: "", model_name: "" };
  }, [selectedManagers, selectedUsers, selectedStore, asoEmpCode]);

  const buildFilterPayload = useCallback(() => {
    const core = buildCoreWeeklyPayload();
    return { ...core, brand: selectedBrand, model_name: selectedModel };
  }, [buildCoreWeeklyPayload, selectedBrand, selectedModel]);

  const weeklyReportStoreLabel = useMemo(() => {
    if (selectedStore == null) return "All stores";
    return selectedStore.label || String(selectedStore.value ?? "");
  }, [selectedStore]);

  /** Payload inside weekly_banner API envelope (`data` field) */
  const weeklyBannerPayload = useMemo(() => {
    if (!weeklyBannerData) return null;
    const err = weeklyBannerData.error;
    const hasRealError =
      err != null &&
      err !== "" &&
      err !== false &&
      !(typeof err === "object" && err !== null && Object.keys(err).length === 0);
    const dataMissing =
      weeklyBannerData.data == null ||
      weeklyBannerData.data === "" ||
      (typeof weeklyBannerData.data === "object" &&
        !Array.isArray(weeklyBannerData.data) &&
        Object.keys(weeklyBannerData.data).length === 0);
    if (hasRealError && dataMissing) return null;
    const inner = weeklyBannerData.data;
    if (inner && typeof inner === "object" && !Array.isArray(inner)) return inner;
    if (weeklyBannerData.growth_opportunities || weeklyBannerData.card_partnership) {
      return weeklyBannerData;
    }
    return null;
  }, [weeklyBannerData]);

  const weeklyBannerErrorMessage = useMemo(() => {
    if (!weeklyBannerData?.error) return null;
    const err = weeklyBannerData.error;
    return typeof err === "string" ? err : err?.message || String(err);
  }, [weeklyBannerData]);

  const weeklyMilestonesUI = useMemo(() => {
    const defaultTitle = "Weekly Milestones Achieved";
    if (!weeklyBannerPayload) {
      return { title: defaultTitle, milestones: [] };
    }
    const bd = weeklyBannerPayload;
    const rawMilestones = Array.isArray(bd.milestones) ? bd.milestones : [];
    return {
      title: bd.section_title || defaultTitle,
      milestones: rawMilestones.slice(0, 3).map((m, i) => ({
        key: String(i),
        label: m.label != null && String(m.label).trim() !== "" ? String(m.label) : "—",
        sublabel: m.sublabel || "",
      })),
    };
  }, [weeklyBannerPayload]);

  const growthOpportunityPoints = useMemo(() => {
    const go = weeklyBannerPayload?.growth_opportunities;
    if (!go || typeof go !== "object") return [];
    return Object.keys(go)
      .filter((k) => /^point_\d+$/.test(k))
      .sort((a, b) => {
        const na = parseInt(a.replace("point_", ""), 10) || 0;
        const nb = parseInt(b.replace("point_", ""), 10) || 0;
        return na - nb;
      })
      .map((k) => go[k])
      .filter((t) => t != null && String(t).trim() !== "");
  }, [weeklyBannerPayload]);

  const growthSectionTitle =
    weeklyBannerPayload?.section_title_growth_opport || "Growth Opportunities";

  const fetchWeeklyChart = useCallback(async (chart_name, filters) => {
    const response = await Axios.post(
      config.get("API_URL") + "/summaryReport/weekly_chart",
      {
        team_ids: filters.team_ids,
        manager_code: filters.manager_codes,
        store_codes: filters.store_codes,
        brand: filters.brand,
        model_name: filters.model_name,
        chart_name,
      },
      { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
    );
    return response?.data;
  }, []);

  const fetchWeeklyBanner = useCallback(async (filters) => {
    const response = await Axios.post(
      config.get("API_URL") + "/summaryReport/weekly_banner",
      {
        team_ids: filters.team_ids,
        manager_code: filters.manager_codes,
        store_codes: filters.store_codes,
        brand: filters.brand,
        model_name: filters.model_name,
      },
      { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
    );
    return response?.data;
  }, []);

  const resetWeeklyCharts = useCallback(() => {
    setWeeklyBannerData(null);
    setTransactionOverallData([]);
    setTransactionOverallInsight([]);
    setTxnTypeData([]);
    setTxnTypeInsight([]);
    setBrandData([]);
    setBrandInsight([]);
    setBodData([]);
    setBodInsight([]);
    setWeeklyChartTitles({ ...WEEKLY_CHART_FALLBACK_TITLES });
    setOverallLoading(false);
    setTxnTypeLoading(false);
    setBrandLoading(false);
    setBodLoading(false);
  }, []);

  // Re-fetch chart data when filters change
  useEffect(() => {
    if (filterRootIds === undefined) return;
    if (!filtersHydrated) return;
    if (isFilterChanging) return;
    if (storeList.length === 0) {
      resetWeeklyCharts();
      setWeeklyBannerLoading(false);
      return;
    }
    if (selectedStore == null) {
      resetWeeklyCharts();
      setWeeklyBannerLoading(false);
      return;
    }
    let chartsCancelled = false;
    setOverallLoading(true);
    setTxnTypeLoading(true);
    setBrandLoading(true);
    setBodLoading(true);

    const payload = buildCoreWeeklyPayload();
    const effectiveFilters = {
      manager_codes: payload.manager_codes,
      team_ids: payload.team_ids,
      store_codes: payload.store_codes,
      brand: payload.brand,
      model_name: payload.model_name,
    };

    // weekly_banner requires store_code in Flask; we default-select first store when list loads
    if (effectiveFilters.store_codes.length > 0) {
      setWeeklyBannerLoading(true);
      fetchWeeklyBanner(effectiveFilters)
        .then(setWeeklyBannerData)
        .catch((e) => {
          console.error("[StorePerformance] weekly_banner error", e);
          setWeeklyBannerData({
            error:
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load weekly banner",
            data: null,
          });
        })
        .finally(() => setWeeklyBannerLoading(false));
    } else {
      setWeeklyBannerData(null);
      setWeeklyBannerLoading(false);
    }

    fetchWeeklyChart("weekly_txn_overall", effectiveFilters)
      .then((overallRes) => {
        if (chartsCancelled) return;
        setWeeklyChartTitles((prev) => ({ ...prev, overall: overallRes?.display_name || WEEKLY_CHART_FALLBACK_TITLES.overall }));
        const overallRows = overallRes?.data?.chart_data || [];
        setTransactionOverallData(
          overallRows.map((r) => {
            const tc = r.transaction_count;
            const tv = r.transaction_volume;
            return {
              week: r.week_label,
              Count: tc,
              Volume: tv,
              formattedCount: formatCountShort(tc),
              formattedVolume: formatCountShort(tv),
              countTooltip:
                tc != null && tc !== "" && Number.isFinite(Number(tc))
                  ? Number(tc).toLocaleString("en-IN", { maximumFractionDigits: 0 })
                  : "",
              volumeTooltip:
                tv != null && tv !== "" && Number.isFinite(Number(tv))
                  ? Number(tv).toLocaleString("en-IN", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })
                  : "",
            };
          })
        );
        setTransactionOverallInsight(overallRes?.data?.insight || []);
      })
      .catch((e) => {
        if (!chartsCancelled) console.error("[StorePerformance] weekly_txn_overall error", e);
      })
      .finally(() => { if (!chartsCancelled) setOverallLoading(false); });

    fetchWeeklyChart("weekly_txn_type", effectiveFilters)
      .then((typeRes) => {
        if (chartsCancelled) return;
        setWeeklyChartTitles((prev) => ({ ...prev, txnType: typeRes?.display_name || WEEKLY_CHART_FALLBACK_TITLES.txnType }));
        const typeRows = typeRes?.data?.chart_data || [];
        setTxnTypeData(
          typeRows.map((r) => ({
            week: r.week_label,
            "Brand EMI": r.brand_emi_volume,
            "Wallet EMI": r.wallet_emi_volume,
            EFT: r.eft_volume,
          }))
        );
        setTxnTypeInsight(typeRes?.data?.insight || []);
      })
      .catch((e) => {
        if (!chartsCancelled) console.error("[StorePerformance] weekly_txn_type error", e);
      })
      .finally(() => { if (!chartsCancelled) setTxnTypeLoading(false); });

    fetchWeeklyChart("weekly_txn_brand", effectiveFilters)
      .then((brandRes) => {
        if (chartsCancelled) return;
        setWeeklyChartTitles((prev) => ({ ...prev, brand: brandRes?.display_name || WEEKLY_CHART_FALLBACK_TITLES.brand }));
        const brandRows = brandRes?.data?.chart_data || [];
        setBrandData(
          brandRows.map((r) => ({
            week: r.week_label,
            Oppo: r.oppo_volume,
            Samsung: r.samsung_volume,
            Vivo: r.vivo_volume,
            Xiaomi: r.xiaomi_volume,
            Realme: r.realme_volume,
          }))
        );
        setBrandInsight(brandRes?.data?.insight || []);
      })
      .catch((e) => {
        if (!chartsCancelled) console.error("[StorePerformance] weekly_txn_brand error", e);
      })
      .finally(() => { if (!chartsCancelled) setBrandLoading(false); });

    fetchWeeklyChart("weekly_bod_txn", effectiveFilters)
      .then((bodRes) => {
        if (chartsCancelled) return;
        setWeeklyChartTitles((prev) => ({ ...prev, bod: bodRes?.display_name || WEEKLY_CHART_FALLBACK_TITLES.bod }));
        const bodRows = bodRes?.data?.chart_data || [];
        const brandColorMap = {
          samsung: "#0d47a1",
          vivo: "#e67e22",
          oppo: "#27ae60",
          xiaomi: "#f1c40f",
          realme: "#7d2181",
        };
        const bodAgg = new Map();
        for (const r of bodRows) {
          const rawName = String(r.campaign_name || "");
          const campaign_name = rawName.replace(/\s+/g, " ").trim();
          if (!campaign_name) continue;
          const volume = Number(r.volume || 0);
          const brandRaw = String(r.campaign_brand || "");
          const campaign_brand = brandRaw.replace(/\s+/g, " ").trim().toLowerCase();
          const existing = bodAgg.get(campaign_name) || { volume: 0, brand: campaign_brand, maxSingle: 0 };
          existing.volume += volume;
          if (volume > existing.maxSingle) {
            existing.brand = campaign_brand;
            existing.maxSingle = volume;
          }
          bodAgg.set(campaign_name, existing);
        }
        setBodData(
          Array.from(bodAgg.entries()).map(([campaign_name, agg], i) => ({
            bod: campaign_name,
            Count: agg.volume,
            formattedCount: formatCountShort(agg.volume),
            color: brandColorMap[agg.brand] || ["#3498db", "#2ecc71", "#e74c3c"][i % 3],
          }))
        );
        setBodInsight(bodRes?.data?.insight || []);
      })
      .catch((e) => {
        if (!chartsCancelled) console.error("[StorePerformance] weekly_bod_txn error", e);
      })
      .finally(() => { if (!chartsCancelled) setBodLoading(false); });

    return () => {
      chartsCancelled = true;
    };
  }, [
    filterRootIds,
    filtersHydrated,
    isFilterChanging,
    storeList.length,
    selectedStore,
    buildCoreWeeklyPayload,
    fetchWeeklyBanner,
    fetchWeeklyChart,
    resetWeeklyCharts,
  ]);

  // Model mix: only this chart should receive brand/model; enable when API exists (see WEEKLY_MODEL_MIX_CHART_ENABLED).
  useEffect(() => {
    if (!WEEKLY_MODEL_MIX_CHART_ENABLED) return;
    if (filterRootIds === undefined || !filtersHydrated) return;
    if (storeList.length === 0 || selectedStore == null) return;
    let cancelled = false;
    const filters = buildFilterPayload();
    fetchWeeklyChart("weekly_model_mix", filters)
      .then(() => {
        if (cancelled) return;
        // Map response into model-mix chart state when wiring the API.
      })
      .catch((e) => {
        if (!cancelled) console.error("[StorePerformance] weekly_model_mix error", e);
      });
    return () => {
      cancelled = true;
    };
  }, [
    filterRootIds,
    filtersHydrated,
    storeList.length,
    selectedStore,
    selectedBrand,
    selectedModel,
    buildFilterPayload,
    fetchWeeklyChart,
  ]);

  const fetchWeeklyModelMix = useCallback(async (storeCodes, brand, merchantSku) => {
    const response = await Axios.post(
      config.get("API_URL") + "/summaryReport/weekly_model_mix",
      { store_codes: storeCodes, brand, merchant_sku: merchantSku },
      { headers: { Authorization: ` ${localStorage.getItem("usertoken")}` } }
    );
    return response?.data;
  }, []);

  useEffect(() => {
    if (filterRootIds === undefined || !filtersHydrated) return;
    if (!selectedStore || !selectedBrand || !selectedModel) {
      setModelMixData([]);
      setModelMixInsight("");
      setModelMixTitle("Pricing Insight");
      return;
    }
    let cancelled = false;
    setModelMixLoading(true);
    fetchWeeklyModelMix([selectedStore.value], selectedBrand, selectedModel)
      .then((res) => {
        if (cancelled) return;
        setModelMixTitle(res?.display_name || "Pricing Insight");
        const rows = res?.data?.chart_data || [];
        setModelMixData(
          rows.map((r) => ({
            week: r.week_label,
            You: r.you_price,
            Market: r.market_price,
            Online: r.online_price,
          }))
        );
        setModelMixInsight(res?.data?.insight || "");
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("[StorePerformance] weekly_model_mix error", e);
          setModelMixData([]);
          setModelMixInsight("");
        }
      })
      .finally(() => {
        if (!cancelled) setModelMixLoading(false);
      });
    return () => { cancelled = true; };
  }, [filterRootIds, filtersHydrated, selectedStore, selectedBrand, selectedModel, fetchWeeklyModelMix]);

  const openExpand = useCallback((title, data, x_axis, y_axis, type, insight = [], insightText = "") => {
    setExpandedChart({ title, data, x_axis, y_axis, type, insight, insightText });
  }, []);

  const closeExpand = useCallback(() => {
    setExpandedChart(null);
  }, []);

  const exportPDF = async () => {
    const element = dashboardRef.current;
    if (!element) return;
    setPdfExporting(true);
    try {
      // Let layout + AmCharts settle; forcing a narrow windowWidth in html2canvas breaks axis scaling.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = 210;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", [pdfWidth, pdfHeight]);
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      const storeName = selectedStore ? selectedStore.label : "All_Stores";
      let dateRange = "Latest";
      if (transactionOverallData && transactionOverallData.length > 0) {
        const firstWeek = transactionOverallData[0].week;
        const lastWeek = transactionOverallData[transactionOverallData.length - 1].week;
        // Assuming week format is "DD-MMM to DD-MMM" or similar
        const startDate = firstWeek.split(" to ")[0] || firstWeek;
        const endDate = lastWeek.includes(" to ") ? lastWeek.split(" to ")[1] : lastWeek;
        dateRange = `${startDate}_${endDate}`;
      }
      const safeStoreName = storeName.replace(/\//g, "-");
      pdf.save(`${safeStoreName}_Performance_${dateRange}.pdf`);
    } catch (error) {
      console.error("PDF Export failed:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setPdfExporting(false);
    }
  };

  const formatCountShort = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return "";
    const abs = Math.abs(num);
    if (abs >= 1_000_000_000) return `${Math.round(num / 1_000_000_000)}b`;
    if (abs >= 1_000_000) return `${Math.round(num / 1_000_000)}m`;
    if (abs >= 1_000) return `${Math.round(num / 1_000)}k`;
    return `${num}`;
  };

  const computeNiceMax = (maxVal) => {
    const n = Number(maxVal);
    if (!Number.isFinite(n) || n <= 0) return 1;
    const exp = Math.floor(Math.log10(n));
    const base = Math.pow(10, exp);
    const fraction = n / base;
    let niceFraction = 1;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
    return niceFraction * base;
  };

  const overallCountMax = computeNiceMax(
    Math.max(0, ...(transactionOverallData || []).map((d) => Number(d.Count || 0)))
  );
  const overallTrendMax = computeNiceMax(
    Math.max(0, ...(transactionOverallData || []).map((d) => Number(d.Volume || 0)))
  );

  const txnTypeMax = computeNiceMax(
    Math.max(
      0,
      ...(txnTypeData || []).map((d) =>
        Math.max(
          Number(d["Brand EMI"] || 0),
          Number(d["Wallet EMI"] || 0),
          Number(d.EFT || 0)
        )
      )
    )
  );

  const brandMax = computeNiceMax(
    Math.max(
      0,
      ...(brandData || []).map((d) =>
        Math.max(
          Number(d.Oppo || 0),
          Number(d.Samsung || 0),
          Number(d.Vivo || 0),
          Number(d.Xiaomi || 0),
          Number(d.Realme || 0)
        )
      )
    )
  );

  const bodMax = computeNiceMax(
    Math.max(0, ...(bodData || []).map((d) => Number(d.Count || 0)))
  );

  const modelMixMax = computeNiceMax(
    Math.max(
      0,
      ...(modelMixData || []).map((d) =>
        Math.max(Number(d.You || 0), Number(d.Online || 0), Number(d.Market || 0))
      )
    )
  );

  const isProcessing =
    filterRootIds === undefined ||
    !filtersHydrated ||
    loading ||
    pdfExporting;

  const storeGateMessage = useMemo(() => {
    if (!filtersHydrated || loading) return null;
    if (selectedStore != null) return null;
    if (storeList.length === 0) return "No store available.";
    return "Please select a store for seeing store performance";
  }, [filtersHydrated, loading, storeList, selectedStore]);

  const canShowWeeklyCharts =
    filtersHydrated && selectedStore != null && storeList.length > 0;

  return (
    <div className="store-performance-container">
      {/* Expand Modal */}
      {expandedChart && (
        <div className="sp-expand-modal-overlay" onClick={closeExpand}>
          <div className="sp-expand-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-expand-modal-header">
              <span className="sp-expand-modal-title">{expandedChart.title}</span>
              <button className="sp-expand-modal-close" onClick={closeExpand}>
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="sp-expand-modal-body">
              {(expandedChart?.insight?.length > 0 || expandedChart?.insightText) && (
                <div className="sp-chart-summary sp-expand-summary">
                  <span className="sp-summary-label">Summary:</span>
                  {expandedChart.insight?.length > 0
                    ? expandedChart.insight.map((item, idx) => (
                        <WeeklyInsightBullet key={idx} item={item} />
                      ))
                    : <div className="sp-summary-text-no-bullet">{expandedChart.insightText}</div>
                  }
                </div>
              )}
              {expandedChart && (
                <AmCharts.React
                  style={{ width: "100%", height: "420px" }}
                  options={{
                    type: "serial",
                    theme: expandedChart.type === "line" ? "light" : "none",
                    marginTop: 10, marginRight: 15, marginLeft: 10, marginBottom: 10,
                    dataProvider: expandedChart.data,
                    graphs: expandedChart.type === "combo"
                      ? [
                          {
                            title: "Transaction Count",
                            valueField: "Count",
                            type: "column",
                            fillAlphas: 0.9,
                            lineAlpha: 0,
                            columnWidth: 0.65,
                            fillColors: "#7d2181",
                            valueAxis: "v1",
                            labelText: "[[Count]]",
                            labelPosition: "top",
                            fontSize: 10,
                            showAllValueLabels: true,
                            balloonText:
                              "<b>Transaction Count</b>: [[countTooltip]]<br/><b>Volume</b>: [[volumeTooltip]]",
                          },
                          {
                            title: "Volume",
                            valueField: "Volume",
                            type: "line",
                            lineColor: "#f39c12",
                            bullet: "round",
                            bulletSize: 4,
                            lineThickness: 2,
                            valueAxis: "v2",
                            balloonText:
                              "<b>Transaction Count</b>: [[countTooltip]]<br/><b>Volume</b>: [[volumeTooltip]]",
                          },
                        ]
                      : expandedChart.y_axis.map((field) => ({
                          title: field,
                          valueField: field,
                          type: expandedChart.type === "column" ? "column" : "line",
                          fillAlphas: expandedChart.type === "column" || expandedChart.type === "stacked" ? 0.9 : 0,
                          bullet: expandedChart.type === "line" ? "round" : undefined,
                          bulletSize: 4,
                          lineThickness: 2,
                          columnWidth: 0.7,
                          balloonText: "[[title]]: [[value]]",
                        })),
                    valueAxes: expandedChart.type === "combo"
                      ? [
                          {
                            id: "v1",
                            position: "left",
                            axisAlpha: 0.3,
                            gridAlpha: 0.1,
                            minimum: 0,
                            fontSize: 10,
                            labelFunction: (v) => formatCountShort(v),
                          },
                          {
                            id: "v2",
                            position: "right",
                            axisAlpha: 0.3,
                            gridAlpha: 0,
                            minimum: 0,
                            fontSize: 10,
                            labelFunction: (v) => formatCountShort(v),
                          },
                        ]
                      : [{ stackType: expandedChart.type === "stacked" ? "regular" : "none", fontSize: 10, labelFunction: (v) => v >= 1000 ? (v/1000)+"k" : v }],
                    legend: { useGraphSettings: true, align: "center", fontSize: 10, position: "bottom", valueWidth: 0 },
                    categoryField: expandedChart.x_axis,
                    categoryAxis: (() => {
                      const count = Array.isArray(expandedChart?.data) ? expandedChart.data.length : 8;
                      return {
                        gridPosition: "start",
                        labelRotation: 45,
                        fontSize: 6,
                        autoGridCount: false,
                        gridCount: count > 0 ? count : 8,
                        axisColor: "#ccc",
                        gridAlpha: 0,
                        labelOffset: 0,
                      };
                    })(),
                    export: { enabled: false }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Header is outside PDF capture (ref on sp-dashboard-content below) */}
      <div className="dashboard-header-compact">
        <div className="header-left">
          <i className="fa fa-angle-left back-icon" onClick={() => history.goBack()}></i>
          <span className="header-title">Store Performance</span>
        </div>
        <div className="header-right">
          <button
            type="button"
            onClick={() => setShowFilters((f) => !f)}
            className="btn btn-outline-secondary btn-filter"
            aria-expanded={showFilters}
            aria-controls="sp-filter-collapse-panel"
            disabled={isProcessing}
          >
            <Funnel size={14} style={{ verticalAlign: "middle" }} />
            Filters
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-filter"
            onClick={exportPDF}
            disabled={isProcessing}
          >
            <i className="fa fa-download" aria-hidden="true" />
            Download
          </button>
        </div>
      </div>

      <div
        className="sp-dashboard-content"
        ref={dashboardRef}
        style={{ position: "relative", minHeight: isProcessing ? "min(70vh, 560px)" : undefined }}
      >
        {isProcessing && (
          <div
            className="sp-processing-overlay"
            aria-busy="true"
            aria-live="polite"
            data-html2canvas-ignore="true"
          >
            <BeatLoader css={beatLoaderOverride} sizeUnit="px" size={15} color="#4b3092" loading />
            <span className="sp-processing-label">
              {pdfExporting ? "Generating PDF…" : "Processing..."}
            </span>
          </div>
        )}

        {showFilters && (
          <div id="sp-filter-collapse-panel" className="filter-panel-wrapper" data-html2canvas-ignore="true">
            <Row className="dashboard-filter-panel">
              {!isICUser && (
                <Col md={3} xs={12} className="mb-2">
                  <label className="form-label">Managers:</label>
                  <ReactMultiSelectCheckboxes
                    options={managerList}
                    value={selectedManagers}
                    onChange={handleManagersChange}
                    components={{ MenuList: VirtualizedMenuList }}
                    styles={customStyles}
                    className="report-listbox"
                    isClearable={true}
                    placeholder="Select managers..."
                    isLoading={loading}
                    isDisabled={isProcessing}
                  />
                </Col>
              )}
              <Col md={isICUser ? 4 : 3} xs={12} className="mb-2">
                <label className="form-label">MGC Team Members:</label>
                <ReactMultiSelectCheckboxes
                  options={userList}
                  value={selectedUsers}
                  onChange={handleTeamMembersChange}
                  components={{ MenuList: VirtualizedMenuList }}
                  styles={customStyles}
                  className="report-listbox"
                  isClearable={false}
                  placeholder="Select team members..."
                  isLoading={loading}
                  isDisabled={isProcessing}
                />
              </Col>

              {/* Pin Codes multi-select searchable dropdown */}
              <Col md={isICUser ? 4 : 3} xs={12} className="mb-2">
                <label className="form-label">Pin Codes:</label>
                <div className="sp-store-dropdown" ref={pinCodeDropdownRef}>
                  <div
                    className={`sp-store-control${pinCodeDropdownOpen ? " sp-store-control--focused" : ""}${isProcessing || pinCodeList.length === 0 ? " sp-store-control--disabled" : ""}`}
                    onClick={() => {
                      if (!isProcessing && pinCodeList.length > 0) setPinCodeDropdownOpen((v) => !v);
                    }}
                  >
                    <div className="sp-store-value-container">
                      {selectedPinCodes.length > 0 ? (
                        <span className="sp-store-single-value">
                          {selectedPinCodes.length === 1
                            ? selectedPinCodes[0]
                            : `${selectedPinCodes.length} pin codes selected`}
                        </span>
                      ) : (
                        <span className="sp-store-placeholder">
                          {pinCodeList.length === 0 ? "No pin codes available" : "Select pin codes..."}
                        </span>
                      )}
                    </div>
                    <div className="sp-store-indicators">
                      <span className="sp-store-dropdown-indicator">
                        <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                          <path d="M4.516 7.548c.436-.446 1.043-.481 1.576 0l3.908 3.747 3.908-3.747c.533-.481 1.141-.446 1.574 0 .436.445.408 1.197 0 1.615-.406.418-4.695 4.502-4.695 4.502-.217.223-.502.335-.787.335s-.57-.112-.789-.335c0 0-4.287-4.084-4.695-4.502s-.436-1.17 0-1.615z" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  {pinCodeDropdownOpen && (
                    <div className="sp-store-menu">
                      <div className="sp-store-search-wrap">
                        <input
                          ref={pinCodeSearchInputRef}
                          className="sp-store-search-input"
                          type="text"
                          placeholder="Search pin codes..."
                          value={pinCodeSearch}
                          onChange={(e) => setPinCodeSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="sp-store-menu-divider" />
                      <div className="sp-store-options-list">
                        {filteredPinCodes.length === 0 ? (
                          <div className="sp-store-no-options">No pin codes found</div>
                        ) : (
                          filteredPinCodes.map((p) => (
                            <div
                              key={p.value}
                              className={`sp-store-option${selectedPinCodes.includes(p.value) ? " sp-store-option--selected" : ""}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handlePinCodeToggle(p.value);
                              }}
                            >
                              {p.label}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {selectedPinCodes.length > 0 && (
                  <div
                    style={{ fontSize: "0.78rem", color: "#7d2181", marginTop: "4px", cursor: "pointer" }}
                    onClick={() => {
                      console.log(`${SP_LOG} clear pin codes (UI)`);
                      setSelectedPinCodes([]);
                      setSelectedStore(null);
                      const { storeIds } = getTeamScopeForStoresAndPins(
                        selectedUsers,
                        selectedManagers,
                        asoEmpCode,
                        filterRootIds,
                        "clearPinCodes"
                      );
                      // When managers are selected but no specific team member, use manager codes for store fetch
                      const managersSelected = selectedManagers && selectedManagers.length > 0;
                      const allIsSelected = (selectedUsers || []).some(u => u.value === 'all');
                      const noSelection = (selectedUsers || []).length === 0;
                      const specificTeamIds = (allIsSelected || noSelection)
                        ? [] : (selectedUsers || []).map(u => u.value).filter(id => id && id !== 'all');
                      const effectiveStoreIds = specificTeamIds.length > 0
                        ? specificTeamIds
                        : managersSelected
                          ? (selectedManagers || []).map(m => m.value)
                          : storeIds;
                      console.log(`${SP_LOG} clear pin codes → refetch stores`, { effectiveStoreIds, pin_codes: [] });
                      fetchStores(effectiveStoreIds, []);
                    }}
                  >
                    Clear pin codes ×
                  </div>
                )}
              </Col>

              <Col md={isICUser ? 4 : 3} xs={12} className="mb-2">
                <label className="form-label">Store Name:</label>
                <div className="sp-store-dropdown" ref={storeDropdownRef}>
                  {/* Control */}
                  <div
                    className={`sp-store-control${storeDropdownOpen ? " sp-store-control--focused" : ""}${isProcessing || storeList.length === 0 ? " sp-store-control--disabled" : ""}`}
                    onClick={() => {
                      if (!isProcessing && storeList.length > 0) setStoreDropdownOpen((v) => !v);
                    }}
                  >
                    <div className="sp-store-value-container">
                      {selectedStore ? (
                        <span className="sp-store-single-value">{selectedStore.label}</span>
                      ) : (
                        <span className="sp-store-placeholder">
                          {storeList.length === 0 ? "No store available" : "Select store..."}
                        </span>
                      )}
                    </div>
                    <div className="sp-store-indicators">
                      <span className="sp-store-dropdown-indicator">
                        <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                          <path d="M4.516 7.548c.436-.446 1.043-.481 1.576 0l3.908 3.747 3.908-3.747c.533-.481 1.141-.446 1.574 0 .436.445.408 1.197 0 1.615-.406.418-4.695 4.502-4.695 4.502-.217.223-.502.335-.787.335s-.57-.112-.789-.335c0 0-4.287-4.084-4.695-4.502s-.436-1.17 0-1.615z" />
                        </svg>
                      </span>
                    </div>
                  </div>

                  {/* Dropdown menu */}
                  {storeDropdownOpen && (
                    <div className="sp-store-menu">
                      <div className="sp-store-search-wrap">
                        <input
                          ref={storeSearchInputRef}
                          className="sp-store-search-input"
                          type="text"
                          placeholder="Search stores..."
                          value={storeSearch}
                          onChange={(e) => setStoreSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="sp-store-menu-divider" />
                      <div className="sp-store-options-list">
                        {filteredStores.length === 0 ? (
                          <div className="sp-store-no-options">No stores found</div>
                        ) : (
                          filteredStores.map((s) => (
                            <div
                              key={String(s.value)}
                              className={`sp-store-option${selectedStore && String(selectedStore.value) === String(s.value) ? " sp-store-option--selected" : ""}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleStoreChange(s);
                              }}
                            >
                              {s.label}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {storeGateMessage && (
                  <div style={{ color: "#888", fontSize: "0.85rem", marginTop: "6px" }}>
                    {storeGateMessage}
                  </div>
                )}
              </Col>
              <Col xs={12} className="mb-2" style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="primary reset-filter"
                  onClick={handleResetFilters}
                  disabled={isProcessing}
                >
                  Reset
                </button>
              </Col>
            </Row>
          </div>
        )}

        <div className="performance-card">
        {canShowWeeklyCharts ? (
          <>
        {/* Weekly Report + milestones (card UI) */}
        <div className="weekly-report-card">
          <div className="weekly-report-card-header">
            <h2 className="weekly-report-card-title">
              Weekly Report – {weeklyReportStoreLabel}
            </h2>
          </div>
          <div className="sp-weekly-divider" />
          <div className="weekly-report-card-body">
            <div className="weekly-report-trophy-watermark" aria-hidden="true">
              <img src={bannerTrophy} alt="" className="weekly-report-trophy-img" crossOrigin="anonymous" />
            </div>
            <div className="weekly-report-body-content">
              <h3 className="weekly-milestones-heading">{weeklyMilestonesUI.title}</h3>
              {weeklyBannerLoading && weeklyBannerPayload == null ? (
                <p className="milestone-empty-msg">Loading milestones…</p>
              ) : weeklyMilestonesUI.milestones.length === 0 ? (
                <p className="milestone-empty-msg">No milestones achieved this week.</p>
              ) : (
                <div className="milestone-grid-left">
                  {weeklyMilestonesUI.milestones.map((m) => (
                    <div key={m.key} className="milestone-box">
                      <span className="milestone-val">{m.label}</span>
                      <span className="milestone-txt">{m.sublabel}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="weekly-growth-block">
                <h3 className="weekly-growth-heading">{growthSectionTitle}</h3>
                {growthOpportunityPoints.length > 0 ? (
                  <ul className="weekly-growth-list">
                    {growthOpportunityPoints.map((text, idx) => (
                      <li key={idx}>{text}</li>
                    ))}
                  </ul>
                ) : weeklyBannerLoading && selectedStore != null ? (
                  <p className="weekly-growth-empty">Loading growth opportunities…</p>
                ) : weeklyBannerErrorMessage ? (
                  <p className="weekly-growth-empty">{weeklyBannerErrorMessage}</p>
                ) : selectedStore == null ? (
                  <p className="weekly-growth-empty">
                    Select a store in Filters to load growth opportunities.
                  </p>
                ) : (
                  <p className="weekly-growth-empty">
                    No growth opportunities returned for this store.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Business Overview — directly under Weekly Milestones (see layout mock) */}
        <h3 className="business-overview-title">Weekly Business Overview</h3>
        <div className="sp-weekly-divider" />
        <Row className="sp-chart-row">
          <Col xs={12} md={6} className="sp-chart-col">
            <div className="sp-chart-card">
              <div className="sp-chart-header">
                <span className="sp-chart-title">{weeklyChartTitles.overall}</span>
                <div className="sp-expand-icon-box" onClick={() => openExpand(weeklyChartTitles.overall, transactionOverallData, "week", ["Count"], "combo", transactionOverallInsight)}><img src={chartExpandIcon} alt="expand" className="sp-expand-icon-img" /></div>
              </div>
              <div className="sp-chart-summary">
                <span className="sp-summary-label">Summary:</span>
                {transactionOverallInsight && transactionOverallInsight.length > 0 ? (
                  transactionOverallInsight.map((item, idx) => (
                    <WeeklyInsightBullet key={idx} item={item} />
                  ))
                ) : (
                  <div className="sp-summary-text-no-bullet">No insights available.</div>
                )}
              </div>
              <div className="sp-chart-area">
                {overallLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <BeatLoader css={beatLoaderOverride} sizeUnit="px" size={10} color="#4b3092" loading />
                  </div>
                ) : <AmCharts.React
                  style={{ width: "100%", height: "100%" }}
                  options={{
                    type: "serial", theme: "none",
                    marginTop: 20, marginRight: 10, marginLeft: 48, marginBottom: 55,
                    dataProvider: transactionOverallData,
                    graphs: [
                      {
                        title: "Transaction Count",
                        valueField: "Count",
                        type: "column",
                        fillColors: "#7d2181",
                        fillAlphas: 0.9,
                        lineAlpha: 0,
                        columnWidth: 0.65,
                        valueAxis: "v1",
                        balloonText:
                          "<b>Transaction Count</b>: [[countTooltip]]<br/><b>Volume</b>: [[volumeTooltip]]",
                        labelText: "[[Count]]",
                        labelPosition: "top",
                        fontSize: 10,
                        showAllValueLabels: true,
                      },
                      {
                        title: "Volume",
                        valueField: "Volume",
                        type: "line",
                        lineColor: "#f39c12",
                        bullet: "round",
                        bulletSize: 3,
                        lineThickness: 2,
                        valueAxis: "v2",
                        balloonText:
                          "<b>Transaction Count</b>: [[countTooltip]]<br/><b>Volume</b>: [[volumeTooltip]]",
                      }
                    ],
                    categoryField: "week",
                    categoryAxis: {
                      gridPosition: "start",
                      labelRotation: 45,
                      fontSize: 9,
                      axisHeight: 22,
                      autoGridCount: false, gridCount: 8,
                      axisColor: "#ccc", gridAlpha: 0, labelOffset: 0
                    },
                    valueAxes: [
                      {
                        id: "v1",
                        position: "left",
                        axisColor: "#ccc",
                        gridColor: "#eee",
                        gridAlpha: 0.5,
                        fontSize: 9,
                        tickLength: 3,
                        minimum: 0,
                        maximum: overallCountMax,
                        autoGridCount: false,
                        gridCount: 3,
                        labelFunction: (v) => v >= 1000 ? (v / 1000) + "k" : v,
                      },
                      {
                        id: "v2",
                        position: "right",
                        axisColor: "#ccc",
                        gridAlpha: 0,
                        fontSize: 9,
                        tickLength: 3,
                        minimum: 0,
                        maximum: overallTrendMax,
                        autoGridCount: false,
                        gridCount: 3,
                        labelFunction: (v) => v >= 1000 ? (v / 1000) + "k" : v,
                      },
                    ],
                    legend: {
                      useGraphSettings: true,
                      align: "center",
                      fontSize: 9,
                      position: "bottom",
                      marginTop: 6,
                      marginBottom: 0,
                      valueWidth: 0,
                      markerSize: 9,
                      verticalGap: 4,
                      horizontalGap: 6,
                      spacing: 4,
                    },
                    export: { enabled: false }
                  }}
                />}
              </div>
            </div>
          </Col>
          <Col xs={12} md={6} className="sp-chart-col">
            <div className="sp-chart-card">
              <div className="sp-chart-header">
                <span className="sp-chart-title">{weeklyChartTitles.txnType}</span>
                <div className="sp-expand-icon-box" onClick={() => openExpand(weeklyChartTitles.txnType, txnTypeData, "week", ["Brand EMI", "Wallet EMI", "EFT"], "line", txnTypeInsight)}><img src={chartExpandIcon} alt="expand" className="sp-expand-icon-img" /></div>
              </div>
              <div className="sp-chart-summary">
                <span className="sp-summary-label">Summary:</span>
                {txnTypeInsight && txnTypeInsight.length > 0 ? (
                  txnTypeInsight.map((item, idx) => (
                    <WeeklyInsightBullet key={idx} item={item} />
                  ))
                ) : (
                  <div className="sp-summary-text-no-bullet">No insights available.</div>
                )}
              </div>
              <div className="sp-chart-area">
                {txnTypeLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <BeatLoader css={beatLoaderOverride} sizeUnit="px" size={10} color="#4b3092" loading />
                  </div>
                ) : <AmCharts.React
                  style={{ width: "100%", height: "100%" }}
                  options={{
                    type: "serial", theme: "none",
                    marginTop: 8, marginRight: 10, marginLeft: 48, marginBottom: 70,
                    dataProvider: txnTypeData,
                    graphs: [
                      { title: "Brand EMI", valueField: "Brand EMI", lineColor: "#f39c12", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Wallet EMI", valueField: "Wallet EMI", lineColor: "#7d2181", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "EFT", valueField: "EFT", lineColor: "#3498db", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                    ],
                    legend: {
                      useGraphSettings: true, align: "center", fontSize: 9,
                      position: "bottom", marginTop: 2, marginBottom: 0,
                      valueWidth: 0, markerSize: 9, verticalGap: 4,
                      horizontalGap: 6, spacing: 4
                    },
                    valueAxes: [{
                      id: "v1", axisColor: "#ccc", gridColor: "#eee", gridAlpha: 0.5,
                      fontSize: 9, tickLength: 3,
                      minimum: 0,
                      maximum: txnTypeMax,
                      autoGridCount: false,
                      gridCount: 3,
                      labelFrequency: 1,
                      axisAlpha: 0.3,
                      ignoreAxisWidth: false,
                      gridAlpha: 0.1,
                      labelFunction: (v) => formatCountShort(v)
                    }],
                    categoryField: "week",
                    categoryAxis: {
                      labelRotation: 45,
                      fontSize: 9,
                      axisHeight: 22,
                      autoGridCount: false, gridCount: 8,
                      axisColor: "#ccc", gridAlpha: 0, labelOffset: 0
                    },
                    export: { enabled: false }
                  }}
                />}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="sp-chart-row">
          <Col xs={12} md={6} className="sp-chart-col">
            <div className="sp-chart-card">
              <div className="sp-chart-header">
                <span className="sp-chart-title">{weeklyChartTitles.brand}</span>
                <div className="sp-expand-icon-box" onClick={() => openExpand(weeklyChartTitles.brand, brandData, "week", ["Oppo", "Samsung", "Vivo", "Xiaomi", "Realme"], "line", brandInsight)}><img src={chartExpandIcon} alt="expand" className="sp-expand-icon-img" /></div>
              </div>
              <div className="sp-chart-summary">
                <span className="sp-summary-label">Summary:</span>
                {brandInsight && brandInsight.length > 0 ? (
                  brandInsight.map((item, idx) => (
                    <WeeklyInsightBullet key={idx} item={item} />
                  ))
                ) : (
                  <div className="sp-summary-text-no-bullet">No insights available.</div>
                )}
              </div>
              <div className="sp-chart-area">
                {brandLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <BeatLoader css={beatLoaderOverride} sizeUnit="px" size={10} color="#4b3092" loading />
                  </div>
                ) : <AmCharts.React
                  style={{ width: "100%", height: "100%" }}
                  options={{
                    type: "serial", theme: "none",
                    marginTop: 8, marginRight: 10, marginLeft: 48, marginBottom: 70,
                    dataProvider: brandData,
                    valueAxes: [{
                      id: "v1", axisColor: "#ccc", gridColor: "#eee", gridAlpha: 0.5,
                      fontSize: 9, tickLength: 3,
                      minimum: 0,
                      maximum: brandMax,
                      autoGridCount: false,
                      gridCount: 3,
                      labelFrequency: 1,
                      axisAlpha: 0.3,
                      ignoreAxisWidth: false,
                      gridAlpha: 0.1,
                      labelFunction: (v) => formatCountShort(v)
                    }],
                    graphs: [
                      { title: "Oppo",    valueField: "Oppo",    valueAxis: "v1", lineColor: "#27ae60", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Samsung", valueField: "Samsung", valueAxis: "v1", lineColor: "#0d47a1", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Vivo",    valueField: "Vivo",    valueAxis: "v1", lineColor: "#3498db", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Xiaomi",  valueField: "Xiaomi",  valueAxis: "v1", lineColor: "#e67e22", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Realme",  valueField: "Realme",  valueAxis: "v1", lineColor: "#f1c40f", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                    ],
                    legend: {
                      useGraphSettings: true, align: "center", fontSize: 9,
                      position: "bottom", marginTop: 2, marginBottom: 0,
                      valueWidth: 0, markerSize: 9, verticalGap: 4,
                      horizontalGap: 4, spacing: 4
                    },
                    categoryField: "week",
                    categoryAxis: {
                      labelRotation: 45,
                      fontSize: 9,
                      axisHeight: 22,
                      autoGridCount: false, gridCount: 8,
                      axisColor: "#ccc", gridAlpha: 0, labelOffset: 0
                    },
                    export: { enabled: false }
                  }}
                />}
              </div>
            </div>
          </Col>
          <Col xs={12} md={6} className="sp-chart-col">
            <div className="sp-chart-card">
              <div className="sp-chart-header">
                <span className="sp-chart-title">{weeklyChartTitles.bod}</span>
                <div className="sp-expand-icon-box" onClick={() => openExpand(weeklyChartTitles.bod, bodData, "bod", ["Count"], "column", bodInsight)}><img src={chartExpandIcon} alt="expand" className="sp-expand-icon-img" /></div>
              </div>
              <div className="sp-chart-summary">
                <span className="sp-summary-label">Summary:</span>
                {bodInsight && bodInsight.length > 0 ? (
                  bodInsight.map((item, idx) => (
                    <WeeklyInsightBullet key={idx} item={item} />
                  ))
                ) : (
                  <div className="sp-summary-text-no-bullet">No insights available.</div>
                )}
              </div>
              <div className="sp-chart-area">
                {bodLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <BeatLoader css={beatLoaderOverride} sizeUnit="px" size={10} color="#4b3092" loading />
                  </div>
                ) : <AmCharts.React
                  style={{ width: "100%", height: "100%" }}
                  options={{
                    type: "serial", theme: "none",
                    marginTop: 10,
                    marginRight: 10,
                    marginLeft: 48,
                    marginBottom: 40,
                    dataProvider: bodData,
                    graphs: [{
                      type: "column",
                      valueField: "Count",
                      fillAlphas: 1,
                      lineAlpha: 0,
                      fillColorsField: "color",
                      lineColorField: "color",
                      columnWidth: 0.6,
                      balloonText: "<b>[[category]]</b>: [[value]]",
                      labelText: "[[Count]]",
                      labelPosition: "top",
                      fontSize: 10,
                      showAllValueLabels: true
                    }],
                    categoryField: "bod",
                    categoryAxis: {
                      gridPosition: "start",
                      labelRotation: 45,
                      fontSize: 8,
                      autoGridCount: false,
                      gridCount: 8,
                      axisColor: "#ccc",
                      gridAlpha: 0,
                      labelOffset: 2,
                    },
                    valueAxes: [{
                      axisColor: "#ccc", gridColor: "#eee", gridAlpha: 0.5,
                      fontSize: 9, tickLength: 3,
                      minimum: 0,
                      maximum: bodMax,
                      autoGridCount: false,
                      gridCount: 3,
                      labelFrequency: 1,
                      axisAlpha: 0.3,
                      ignoreAxisWidth: false,
                      gridAlpha: 0.1,
                      labelFunction: (v) => formatCountShort(v)
                    }],
                    export: { enabled: false }
                  }}
                />}
              </div>
            </div>
          </Col>
        </Row>

        <hr className="sp-section-separator" />

        {/* Model Mix Insight */}
        <h3 className="business-overview-title">Model Mix Insight</h3>

        {/* Dropdowns outside the chart card, side by side */}
        <div className="model-mix-filters">
          {/* Brand searchable dropdown */}
          <div className="filter-group-compact">
            <label className="filter-label-compact">Brand</label>
            <div className="sp-store-dropdown" ref={brandDropdownRef}>
              <div
                className={`sp-store-control${brandDropdownOpen ? " sp-store-control--focused" : ""}${brandList.length === 0 ? " sp-store-control--disabled" : ""}`}
                onClick={() => { if (brandList.length > 0) setBrandDropdownOpen((v) => !v); }}
              >
                <div className="sp-store-value-container">
                  {selectedBrand ? (
                    <span className="sp-store-single-value">{selectedBrand}</span>
                  ) : (
                    <span className="sp-store-placeholder">
                      {brandList.length === 0 ? "No brands available" : "Select Brand"}
                    </span>
                  )}
                </div>
                <div className="sp-store-indicators">
                  <span className="sp-store-dropdown-indicator">
                    <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                      <path d="M4.516 7.548c.436-.446 1.043-.481 1.576 0l3.908 3.747 3.908-3.747c.533-.481 1.141-.446 1.574 0 .436.445.408 1.197 0 1.615-.406.418-4.695 4.502-4.695 4.502-.217.223-.502.335-.787.335s-.57-.112-.789-.335c0 0-4.287-4.084-4.695-4.502s-.436-1.17 0-1.615z" />
                    </svg>
                  </span>
                </div>
              </div>
              {brandDropdownOpen && (
                <div className="sp-store-menu">
                  <div className="sp-store-search-wrap">
                    <input
                      ref={brandSearchInputRef}
                      className="sp-store-search-input"
                      type="text"
                      placeholder="Search brands..."
                      value={brandSearch}
                      onChange={(e) => setBrandSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="sp-store-menu-divider" />
                  <div className="sp-store-options-list">
                    {filteredBrands.length === 0 ? (
                      <div className="sp-store-no-options">No brands found</div>
                    ) : (
                      filteredBrands.map((b, idx) => (
                        <div
                          key={idx}
                          className={`sp-store-option${selectedBrand === b.brand ? " sp-store-option--selected" : ""}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedBrand(b.brand);
                            setBrandDropdownOpen(false);
                            setBrandSearch("");
                          }}
                        >
                          {b.brand}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Model searchable dropdown */}
          <div className="filter-group-compact">
            <label className="filter-label-compact">Model</label>
            <div className="sp-store-dropdown" ref={modelDropdownRef}>
              <div
                className={`sp-store-control${modelDropdownOpen ? " sp-store-control--focused" : ""}${!selectedBrand || modelList.length === 0 ? " sp-store-control--disabled" : ""}`}
                onClick={() => { if (selectedBrand && modelList.length > 0) setModelDropdownOpen((v) => !v); }}
              >
                <div className="sp-store-value-container">
                  {selectedModel ? (
                    <span className="sp-store-single-value">
                      {modelList.find((m) => m.sku === selectedModel)?.txn_model_name || selectedModel}
                    </span>
                  ) : (
                    <span className="sp-store-placeholder">
                      {!selectedBrand ? "Select a brand first" : modelList.length === 0 ? "No models available" : "Select Model"}
                    </span>
                  )}
                </div>
                <div className="sp-store-indicators">
                  <span className="sp-store-dropdown-indicator">
                    <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                      <path d="M4.516 7.548c.436-.446 1.043-.481 1.576 0l3.908 3.747 3.908-3.747c.533-.481 1.141-.446 1.574 0 .436.445.408 1.197 0 1.615-.406.418-4.695 4.502-4.695 4.502-.217.223-.502.335-.787.335s-.57-.112-.789-.335c0 0-4.287-4.084-4.695-4.502s-.436-1.17 0-1.615z" />
                    </svg>
                  </span>
                </div>
              </div>
              {modelDropdownOpen && (
                <div className="sp-store-menu">
                  <div className="sp-store-search-wrap">
                    <input
                      ref={modelSearchInputRef}
                      className="sp-store-search-input"
                      type="text"
                      placeholder="Search models..."
                      value={modelSearch}
                      onChange={(e) => setModelSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="sp-store-menu-divider" />
                  <div className="sp-store-options-list">
                    {filteredModels.length === 0 ? (
                      <div className="sp-store-no-options">No models found</div>
                    ) : (
                      filteredModels.map((m, idx) => (
                        <div
                          key={idx}
                          className={`sp-store-option${selectedModel === m.sku ? " sp-store-option--selected" : ""}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedModel(m.sku);
                            setModelDropdownOpen(false);
                            setModelSearch("");
                          }}
                        >
                          {m.txn_model_name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Row className="sp-chart-row">
          <Col xs={12} md={6} className="sp-chart-col">
            <div className="sp-chart-card">
              <div className="sp-chart-header">
                <span className="sp-chart-title">{modelMixTitle}</span>
                <div className="sp-expand-icon-box" onClick={() => openExpand(modelMixTitle, modelMixData, "week", ["You", "Market", "Online"], "line", [], modelMixInsight)}><img src={chartExpandIcon} alt="expand" className="sp-expand-icon-img" /></div>
              </div>
              <div className="sp-chart-summary">
                <span className="sp-summary-label">Summary:</span>
                {modelMixInsight ? (
                  <div className="sp-summary-text-no-bullet">{modelMixInsight}</div>
                ) : (
                  <div className="sp-summary-text-no-bullet">
                    {(!selectedBrand || !selectedModel) ? "Select a brand and model to view pricing insight." : "No insights available."}
                  </div>
                )}
              </div>
              <div className="sp-chart-area">
                {modelMixLoading ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <BeatLoader css={beatLoaderOverride} sizeUnit="px" size={10} color="#4b3092" loading />
                  </div>
                ) : (
                <AmCharts.React
                  style={{ width: "100%", height: "100%" }}
                  options={{
                    type: "serial", theme: "none",
                    marginTop: 8, marginRight: 10, marginLeft: 48, marginBottom: 70,
                    dataProvider: modelMixData,
                    graphs: [
                      { title: "You", valueField: "You", type: "line", lineColor: "#f1c40f", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Online", valueField: "Online", type: "line", lineColor: "#e74c3c", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                      { title: "Market", valueField: "Market", type: "line", lineColor: "#3498db", bullet: "round", bulletSize: 3, lineThickness: 2, balloonText: "[[title]]: [[value]]", fillAlphas: 0 },
                    ],
                    valueAxes: [{
                      id: "v1",
                      stackType: "none",
                      axisAlpha: 0.3,
                      gridAlpha: 0.1,
                      fontSize: 9,
                      tickLength: 3,
                      minimum: 0,
                      maximum: modelMixMax,
                      autoGridCount: false,
                      gridCount: 3,
                      labelFrequency: 1,
                      labelFunction: (v) => formatCountShort(v),
                    }],
                    legend: {
                      useGraphSettings: true, align: "center", fontSize: 9,
                      position: "bottom", marginTop: 2, marginBottom: 0,
                      valueWidth: 0, markerSize: 9, verticalGap: 4,
                      horizontalGap: 6, spacing: 4
                    },
                    categoryField: "week",
                    categoryAxis: {
                      gridPosition: "start",
                      labelRotation: 45,
                      fontSize: 9,
                      axisHeight: 22,
                      autoGridCount: false,
                      gridCount: 8,
                      axisColor: "#ccc",
                      gridAlpha: 0,
                      labelOffset: 0,
                    },
                    export: { enabled: false }
                  }}
                />
                )}
              </div>
            </div>
          </Col>
          <Col xs={12} md={6} className="sp-chart-col" />
        </Row>
          </>
        ) : storeGateMessage ? (
          <div className="sp-store-empty" role="status">
            <p className="sp-store-empty-text">{storeGateMessage}</p>
          </div>
        ) : null}
        </div>
      </div>
    </div>
  );
};

export default withRouter(StorePerformance);
